import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import {
  createVendorType,
  updateVendorType,
  deleteVendorType,
} from '../../../services/vendorService';

const TypeListManager = ({ types, onTypesChange }) => {
  const [showAddType, setShowAddType] = useState(false);
  const [newType, setNewType] = useState('');
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [editingTypeLabel, setEditingTypeLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAddType = async () => {
    if (!newType?.trim()) return;
    try {
      setSaving(true);
      const created = await createVendorType(newType?.trim());
      onTypesChange([...types, created]);
      setNewType('');
      setShowAddType(false);
    } catch (err) {
      console.error('Failed to add type:', err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditType = (type) => {
    setEditingTypeId(type?.id);
    setEditingTypeLabel(type?.label);
  };

  const handleSaveType = async (id) => {
    if (!editingTypeLabel?.trim()) return;
    try {
      setSaving(true);
      const updated = await updateVendorType(id, editingTypeLabel?.trim());
      onTypesChange(types?.map(t => t?.id === id ? updated : t));
      setEditingTypeId(null);
      setEditingTypeLabel('');
    } catch (err) {
      console.error('Failed to update type:', err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteType = async (id) => {
    try {
      await deleteVendorType(id);
      onTypesChange(types?.filter(t => t?.id !== id));
    } catch (err) {
      console.error('Failed to delete type:', err?.message);
    }
  };

  const handleKeyDown = (e, action) => {
    if (e?.key === 'Enter') action();
    if (e?.key === 'Escape') {
      setShowAddType(false);
      setEditingTypeId(null);
      setNewType('');
      setEditingTypeLabel('');
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-green-50 border-b border-gray-200 flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Manage Type Options</span>
        <button
          onClick={() => setShowAddType(true)}
          className="text-xs text-green-600 hover:text-green-800 font-medium flex items-center gap-1"
        >
          <Icon name="Plus" size={12} />
          Add Option
        </button>
      </div>
      {showAddType && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50">
          <Input
            type="text"
            placeholder="New type option..."
            value={newType}
            onChange={(e) => setNewType(e?.target?.value)}
            onKeyDown={(e) => handleKeyDown(e, handleAddType)}
            className="flex-1 text-sm"
            autoFocus
          />
          <Button variant="primary" size="xs" onClick={handleAddType} disabled={saving}>Add</Button>
          <Button variant="ghost" size="xs" onClick={() => { setShowAddType(false); setNewType(''); }}>Cancel</Button>
        </div>
      )}
      {types?.length === 0 && !showAddType ? (
        <div className="px-3 py-3 text-xs text-gray-400 text-center">No type options yet. Click "Add Option" to create one.</div>
      ) : (
        <ul className="divide-y divide-gray-100 max-h-48 overflow-y-auto">
          {types?.map((type) => (
            <li key={type?.id} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50">
              {editingTypeId === type?.id ? (
                <div className="flex items-center gap-2 flex-1">
                  <Input
                    type="text"
                    value={editingTypeLabel}
                    onChange={(e) => setEditingTypeLabel(e?.target?.value)}
                    onKeyDown={(e) => handleKeyDown(e, () => handleSaveType(type?.id))}
                    className="flex-1 text-sm"
                    autoFocus
                  />
                  <Button variant="primary" size="xs" onClick={() => handleSaveType(type?.id)} disabled={saving}>Save</Button>
                  <Button variant="ghost" size="xs" onClick={() => { setEditingTypeId(null); setEditingTypeLabel(''); }}>Cancel</Button>
                </div>
              ) : (
                <>
                  <span className="text-sm text-gray-700">{type?.label}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => handleEditType(type)} className="p-1 text-gray-400 hover:text-gray-600 rounded">
                      <Icon name="Pencil" size={12} />
                    </button>
                    <button onClick={() => handleDeleteType(type?.id)} className="p-1 text-gray-400 hover:text-red-500 rounded">
                      <Icon name="Trash2" size={12} />
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TypeListManager;
